import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { useBoardStore } from '../store/useBoardStore';
import {
  collectCompletedGoalSteps,
  collectHiddenGoalSteps,
} from '../utils/boardContextRecommendations';
import {
  constraintDisplayText,
  constraintKindLabelKey,
  constraintMemoryKey,
} from '../utils/contextMemory';

function uniqueTrimmed(list = []) {
  const out = [];
  const seen = new Set();
  for (const raw of Array.isArray(list) ? list : []) {
    const text = String(raw || '').trim();
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }
  return out;
}

function MemoryRow({
  item,
  selected,
  disabled,
  onToggleSelected,
  onRestore,
  onDelete,
  onToggleActive,
  pendingId,
  messages,
}) {
  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        py: 0.5,
        pl: 0.5,
        pr: 1.25,
        borderRadius: 1,
        border: '1px solid',
        borderColor: selected ? 'error.light' : 'divider',
        bgcolor: selected ? 'rgba(244, 63, 94, 0.04)' : 'transparent',
        opacity: item.active === false ? 0.7 : 1,
      }}
    >
      <Checkbox
        size="small"
        checked={selected}
        disabled={disabled}
        onChange={() => onToggleSelected(item.id)}
        inputProps={{ 'aria-label': item.text }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, mb: 0.15 }}>
          {item.kindLabel ? (
            <Chip
              size="small"
              label={item.kindLabel}
              variant="outlined"
              sx={{ height: 20, fontSize: 11, flexShrink: 0 }}
            />
          ) : null}
          {item.active === false ? (
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {messages.contextMemoryInactive}
            </Typography>
          ) : null}
        </Box>
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={item.text}
        >
          {item.text}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {item.canToggleActive ? (
          <Switch
            size="small"
            checked={item.active !== false}
            disabled={disabled}
            onChange={() => onToggleActive(item)}
            inputProps={{ 'aria-label': messages.contextMemoryToggleActive }}
          />
        ) : null}
        {item.canRestore ? (
          <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={() => onRestore(item)}
          >
            {pendingId === `restore:${item.id}`
              ? `${messages.restoreHiddenStep}…`
              : messages.restoreHiddenStep}
          </Button>
        ) : null}
        {item.canDelete ? (
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={disabled}
            onClick={() => onDelete(item)}
          >
            {pendingId === `delete:${item.id}`
              ? `${messages.deleteHiddenStep}…`
              : messages.deleteHiddenStep}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

function MemoryGroup({ title, items, selectedIds, ...rowProps }) {
  if (!items.length) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        {title}
      </Typography>
      <Box
        component="ul"
        sx={{
          m: 0,
          p: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {items.map(item => (
          <MemoryRow
            key={item.id}
            item={item}
            selected={selectedIds.has(item.id)}
            {...rowProps}
          />
        ))}
      </Box>
    </Box>
  );
}

export default function BoardContextMemorySection({
  isOwner,
  busy,
  messages,
  onError,
}) {
  const userProgress = useBoardStore(
    state => state.board?.contextRecommendations?.userProgress
  );
  const goalHistory = useBoardStore(state => state.board?.goalHistory);
  const constraints = useBoardStore(
    state => state.board?.contextRecommendations?.constraints
  );
  const restoreNotInterestedGoalOption = useBoardStore(
    state => state.restoreNotInterestedGoalOption
  );
  const permanentlyDeleteNotInterestedGoalOption = useBoardStore(
    state => state.permanentlyDeleteNotInterestedGoalOption
  );
  const editContextMemory = useBoardStore(state => state.editContextMemory);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [pendingId, setPendingId] = useState('');

  const notInterestedSteps = useMemo(
    () => collectHiddenGoalSteps({ userProgress }),
    [userProgress]
  );
  const completedSteps = useMemo(
    () => collectCompletedGoalSteps({ goalHistory, userProgress }),
    [goalHistory, userProgress]
  );
  const notInterestedGaps = useMemo(
    () => uniqueTrimmed(userProgress?.notInterestedKeys),
    [userProgress]
  );
  const solvedGaps = useMemo(() => {
    const ni = new Set(notInterestedGaps.map(item => item.toLowerCase()));
    return uniqueTrimmed(userProgress?.doneBlockerKeys)
      .filter(item => !ni.has(item.toLowerCase()));
  }, [userProgress, notInterestedGaps]);
  const validatedClaims = useMemo(
    () => uniqueTrimmed(userProgress?.doneValidationKeys),
    [userProgress]
  );
  const constraintItems = useMemo(
    () => (Array.isArray(constraints) ? constraints : [])
      .map((item, index) => ({ constraint: item, index }))
      .filter(({ constraint }) => constraintDisplayText(constraint)),
    [constraints]
  );

  const groups = useMemo(() => ({
    hidden: notInterestedSteps.map(text => ({
      id: `step:${text}`,
      group: 'step',
      text,
      kindLabel: messages.notInterested,
      canRestore: true,
      canDelete: true,
      value: text,
    })),
    completed: completedSteps.map(text => ({
      id: `completed:${text}`,
      group: 'completed',
      text,
      kindLabel: messages.solved,
      canRestore: true,
      canDelete: true,
      value: text,
    })),
    ni: notInterestedGaps.map(text => ({
      id: `ni:${text}`,
      group: 'ni',
      text,
      kindLabel: messages.notInterested,
      canRestore: true,
      canDelete: false,
      value: text,
    })),
    solved: solvedGaps.map(text => ({
      id: `done:${text}`,
      group: 'done',
      text,
      kindLabel: messages.solved,
      canRestore: true,
      canDelete: false,
      value: text,
    })),
    validated: validatedClaims.map(text => ({
      id: `valid:${text}`,
      group: 'valid',
      text,
      kindLabel: messages.contextMemoryValidatedTitle,
      canRestore: true,
      canDelete: false,
      value: text,
    })),
    claims: constraintItems.map(({ constraint, index }) => {
      const key = constraintMemoryKey(constraint, index);
      return {
        id: `c:${key}`,
        group: 'constraint',
        text: constraintDisplayText(constraint),
        kindLabel: messages[constraintKindLabelKey(constraint)] || messages.constraintKindFact,
        canRestore: false,
        canDelete: true,
        canToggleActive: true,
        active: constraint.active !== false,
        value: key,
      };
    }),
  }), [
    notInterestedSteps,
    completedSteps,
    notInterestedGaps,
    solvedGaps,
    validatedClaims,
    constraintItems,
    messages,
  ]);

  const allItems = useMemo(
    () => [
      ...groups.hidden,
      ...groups.completed,
      ...groups.ni,
      ...groups.solved,
      ...groups.validated,
      ...groups.claims,
    ],
    [groups]
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      if (!prev.size) return prev;
      const alive = new Set(allItems.map(item => item.id));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (alive.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [allItems]);

  const selectedCount = selectedIds.size;
  const allSelected = allItems.length > 0 && selectedCount === allItems.length;
  const disabled = Boolean(pendingId) || busy || !isOwner;

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (!allItems.length) return prev;
      if (prev.size === allItems.length) return new Set();
      return new Set(allItems.map(item => item.id));
    });
  }, [allItems]);

  const runEdit = useCallback(async (pending, work) => {
    setPendingId(pending);
    onError('');
    try {
      await work();
    } catch (err) {
      const fallback = String(pending || '').startsWith('delete:')
        ? messages.contextMemoryDeleteFailed
        : messages.contextMemoryRestoreFailed;
      onError(err?.message || fallback);
    } finally {
      setPendingId('');
    }
  }, [messages.contextMemoryDeleteFailed, messages.contextMemoryRestoreFailed, onError]);

  const handleRestore = useCallback(async (item) => {
    if (!isOwner || pendingId) return;
    if (item.group === 'step' || item.group === 'completed') {
      await runEdit(`restore:${item.id}`, () => restoreNotInterestedGoalOption(item.value));
      return;
    }
    const edits = item.group === 'ni'
      ? { removeNotInterestedKeys: [item.value] }
      : item.group === 'done'
        ? { removeDoneBlockerKeys: [item.value] }
        : item.group === 'valid'
          ? { removeDoneValidationKeys: [item.value] }
          : null;
    if (!edits) return;
    await runEdit(`restore:${item.id}`, () => editContextMemory(edits));
  }, [
    isOwner,
    pendingId,
    runEdit,
    restoreNotInterestedGoalOption,
    editContextMemory,
  ]);

  const handleDelete = useCallback(async (item) => {
    if (!isOwner || pendingId) return;
    if (item.group === 'step' || item.group === 'completed') {
      await runEdit(`delete:${item.id}`, () => permanentlyDeleteNotInterestedGoalOption(item.value));
      return;
    }
    if (item.group === 'constraint') {
      await runEdit(`delete:${item.id}`, () => editContextMemory({
        deleteConstraintKeys: [item.value],
      }));
    }
  }, [
    isOwner,
    pendingId,
    runEdit,
    permanentlyDeleteNotInterestedGoalOption,
    editContextMemory,
  ]);

  const handleToggleActive = useCallback(async (item) => {
    if (!isOwner || pendingId || item.group !== 'constraint') return;
    await runEdit(`toggle:${item.id}`, () => editContextMemory({
      setConstraintActive: {
        keys: [item.value],
        active: item.active === false,
      },
    }));
  }, [isOwner, pendingId, runEdit, editContextMemory]);

  const handleDeleteSelected = useCallback(async () => {
    if (!isOwner || pendingId || !selectedIds.size) return;
    const selected = allItems.filter(item => selectedIds.has(item.id));
    if (!selected.length) return;
    await runEdit('delete:selected', async () => {
      const steps = selected.filter(
        item => item.group === 'step' || item.group === 'completed'
      );
      for (const step of steps) {
        await permanentlyDeleteNotInterestedGoalOption(step.value);
      }
      const edits = {
        removeNotInterestedKeys: selected
          .filter(item => item.group === 'ni')
          .map(item => item.value),
        removeDoneBlockerKeys: selected
          .filter(item => item.group === 'done')
          .map(item => item.value),
        removeDoneValidationKeys: selected
          .filter(item => item.group === 'valid')
          .map(item => item.value),
        deleteConstraintKeys: selected
          .filter(item => item.group === 'constraint')
          .map(item => item.value),
      };
      const hasEdits = edits.removeNotInterestedKeys.length
        || edits.removeDoneBlockerKeys.length
        || edits.removeDoneValidationKeys.length
        || edits.deleteConstraintKeys.length;
      if (hasEdits) await editContextMemory(edits);
      setSelectedIds(new Set());
    });
  }, [
    isOwner,
    pendingId,
    selectedIds,
    allItems,
    runEdit,
    permanentlyDeleteNotInterestedGoalOption,
    editContextMemory,
  ]);

  if (!isOwner) return null;

  const rowProps = {
    disabled,
    onToggleSelected: toggleSelected,
    onRestore: handleRestore,
    onDelete: handleDelete,
    onToggleActive: handleToggleActive,
    pendingId,
    messages,
    selectedIds,
  };

  const groupList = [
    [messages.hiddenStepsTitle, groups.hidden],
    [messages.contextMemoryCompletedTitle, groups.completed],
    [messages.contextMemoryNotInterestedTitle, groups.ni],
    [messages.contextMemorySolvedTitle, groups.solved],
    [messages.contextMemoryValidatedTitle, groups.validated],
    [messages.contextMemoryConstraintsTitle, groups.claims],
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {messages.contextMemoryTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {messages.contextMemoryDescription}
      </Typography>

      {allItems.length ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1,
              flexWrap: 'wrap',
            }}
          >
            <FormControlLabel
              sx={{ ml: 0, mr: 0 }}
              control={(
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  disabled={disabled}
                  onChange={toggleSelectAll}
                  inputProps={{ 'aria-label': messages.selectAllHiddenSteps }}
                />
              )}
              label={(
                <Typography variant="body2" color="text.secondary">
                  {allSelected
                    ? messages.clearHiddenStepSelection
                    : messages.selectAllHiddenSteps}
                </Typography>
              )}
            />
            {selectedCount > 0 ? (
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={disabled}
                onClick={handleDeleteSelected}
              >
                {pendingId === 'delete:selected'
                  ? `${messages.deleteSelectedHiddenSteps(selectedCount)}…`
                  : messages.deleteSelectedHiddenSteps(selectedCount)}
              </Button>
            ) : null}
          </Box>
          <Box sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
            {groupList.map(([title, items]) => (
              <MemoryGroup
                key={title}
                title={title}
                items={items}
                {...rowProps}
              />
            ))}
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {messages.contextMemoryEmpty}
        </Typography>
      )}
    </Box>
  );
}
